import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, Users, Store, ShoppingCart, CreditCard,
  DollarSign, Package, RefreshCw, Calendar, ArrowUpRight, ArrowDownRight,
  Zap, Target, Award, PieChart, BarChart3, Globe, Wallet, Building,
  Clock, CheckCircle, AlertCircle, Layers
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import './PlatformInsights.css';

const COLORS = ['#1a6f63', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#ec4899'];

const PlatformInsights = () => {
  const [insights, setInsights] = useState(null);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [topMerchants, setTopMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currency, setCurrency] = useState('NGN'); // NGN or USD
  const [period, setPeriod] = useState('thisYear');

  const fetchInsights = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [insightsRes, trendRes, merchantsRes] = await Promise.all([
        fetch(`/api/admin/platform-insights?period=${period}`, { credentials: 'include' }),
        fetch('/api/admin/platform-insights/monthly-trend', { credentials: 'include' }),
        fetch('/api/admin/platform-insights/top-merchants?limit=10', { credentials: 'include' })
      ]);

      if (insightsRes.ok) {
        const data = await insightsRes.json();
        if (data.success) setInsights(data.insights);
      }

      if (trendRes.ok) {
        const data = await trendRes.json();
        if (data.success) setMonthlyTrend(data.months);
      }

      if (merchantsRes.ok) {
        const data = await merchantsRes.json();
        if (data.success) setTopMerchants(data.merchants);
      }

    } catch (err) {
      console.error('Error fetching insights:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // Format money based on selected currency
  const formatMoney = (amountNGN, amountUSD, options = {}) => {
    const { compact = false } = options;
    const amount = currency === 'USD' ? amountUSD : amountNGN;
    const symbol = currency === 'USD' ? '$' : '₦';

    if (compact) {
      if (amount >= 1000000000) return `${symbol}${(amount / 1000000000).toFixed(1)}B`;
      if (amount >= 1000000) return `${symbol}${(amount / 1000000).toFixed(1)}M`;
      if (amount >= 1000) return `${symbol}${(amount / 1000).toFixed(1)}K`;
    }

    return `${symbol}${amount.toLocaleString(undefined, {
      minimumFractionDigits: currency === 'USD' ? 2 : 0,
      maximumFractionDigits: currency === 'USD' ? 2 : 0
    })}`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) return <ArrowUpRight size={16} className="text-green-500" />;
    if (growth < 0) return <ArrowDownRight size={16} className="text-red-500" />;
    return null;
  };

  const getGrowthClass = (growth) => {
    if (growth > 0) return 'positive';
    if (growth < 0) return 'negative';
    return 'neutral';
  };

  if (loading) {
    return (
      <div className="insights-loading">
        <RefreshCw className="spin" size={48} />
        <p>Loading platform insights...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="insights-error">
        <AlertCircle size={48} />
        <h3>Error Loading Insights</h3>
        <p>{error}</p>
        <button onClick={fetchInsights}>Retry</button>
      </div>
    );
  }

  if (!insights) return null;

  const { story, merchants, subscriptions, gmv, revenue, settlements, customers, products } = insights;

  // Prepare chart data
  const revenueBreakdownData = [
    { name: 'Subscriptions', value: currency === 'USD' ? revenue.breakdown.subscriptionsUSD : revenue.breakdown.subscriptionsNGN, color: '#1a6f63' },
    { name: 'Service Charges', value: currency === 'USD' ? revenue.breakdown.serviceChargesUSD : revenue.breakdown.serviceChargesNGN, color: '#3b82f6' },
    { name: 'POS Fees', value: currency === 'USD' ? revenue.breakdown.posFeesUSD : revenue.breakdown.posFeesNGN, color: '#8b5cf6' }
  ].filter(d => d.value > 0);

  const tierData = Object.entries(merchants.byTier || {}).map(([tier, count], i) => ({
    name: tier,
    value: count,
    color: COLORS[i % COLORS.length]
  }));

  const channelData = Object.entries(gmv.byChannel || {}).map(([channel, data], i) => ({
    name: channel,
    orders: data.orders,
    gmv: currency === 'USD' ? data.gmv * (insights.usdRate || 0.00063) : data.gmv,
    color: COLORS[i % COLORS.length]
  }));

  return (
    <div className="platform-insights">
      {/* Header */}
      <div className="insights-header">
        <div className="insights-title">
          <h1>Platform Performance</h1>
          <p>Your complete business story at a glance</p>
        </div>
        <div className="insights-controls">
          {/* Currency Toggle */}
          <div className="currency-toggle">
            <button
              className={currency === 'NGN' ? 'active' : ''}
              onClick={() => setCurrency('NGN')}
            >
              ₦ NGN
            </button>
            <button
              className={currency === 'USD' ? 'active' : ''}
              onClick={() => setCurrency('USD')}
            >
              $ USD
            </button>
          </div>

          {/* Period Selector */}
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="thisYear">This Year</option>
            <option value="thisMonth">This Month</option>
            <option value="last30Days">Last 30 Days</option>
          </select>

          <button className="refresh-btn" onClick={fetchInsights}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* The Story - Hero Section */}
      <div className="story-section">
        <div className="story-card">
          <div className="story-icon"><Target size={32} /></div>
          <div className="story-content">
            <span className="story-label">{story.periodLabel}</span>
            <p className="story-headline">
              We onboarded <strong>{formatNumber(story.merchantsOnboarded)}</strong> new merchants,
              processed <strong>{formatNumber(story.ordersProcessed)}</strong> orders,
              and served <strong>{formatNumber(story.customersServed)}</strong> customers.
            </p>
          </div>
        </div>

        <div className="story-metrics">
          <div className="story-metric">
            <Store size={24} />
            <div>
              <span className="metric-value">{formatNumber(merchants.total)}</span>
              <span className="metric-label">Total Merchants</span>
            </div>
          </div>
          <div className="story-metric">
            <ShoppingCart size={24} />
            <div>
              <span className="metric-value">{formatNumber(gmv.orders)}</span>
              <span className="metric-label">Orders</span>
            </div>
          </div>
          <div className="story-metric">
            <Users size={24} />
            <div>
              <span className="metric-value">{formatNumber(customers.total)}</span>
              <span className="metric-label">Customers</span>
            </div>
          </div>
          <div className="story-metric">
            <Package size={24} />
            <div>
              <span className="metric-value">{formatNumber(products.total)}</span>
              <span className="metric-label">Products</span>
            </div>
          </div>
        </div>
      </div>

      {/* Key Financial Metrics */}
      <div className="metrics-grid">
        {/* GMV Card */}
        <div className="metric-card gmv">
          <div className="metric-header">
            <DollarSign size={20} />
            <span className={`metric-growth ${getGrowthClass(gmv.growth)}`}>
              {getGrowthIcon(gmv.growth)}
              {Math.abs(gmv.growth)}%
            </span>
          </div>
          <div className="metric-value-large">
            {formatMoney(gmv.totalNGN, gmv.totalUSD, { compact: true })}
          </div>
          <div className="metric-label">Gross Merchandise Value</div>
          <div className="metric-sub">
            Avg Order: {formatMoney(gmv.avgOrderValueNGN, gmv.avgOrderValueUSD)}
          </div>
        </div>

        {/* Platform Revenue Card */}
        <div className="metric-card revenue">
          <div className="metric-header">
            <Wallet size={20} />
            <span className={`metric-growth ${getGrowthClass(revenue.growth)}`}>
              {getGrowthIcon(revenue.growth)}
              {Math.abs(revenue.growth)}%
            </span>
          </div>
          <div className="metric-value-large">
            {formatMoney(revenue.totalNGN, revenue.totalUSD, { compact: true })}
          </div>
          <div className="metric-label">Platform Revenue</div>
          <div className="metric-sub">
            Subscriptions + Fees
          </div>
        </div>

        {/* Subscription Revenue Card */}
        <div className="metric-card subscriptions">
          <div className="metric-header">
            <CreditCard size={20} />
            <span className="metric-badge">{subscriptions.active} active</span>
          </div>
          <div className="metric-value-large">
            {formatMoney(subscriptions.revenueNGN, subscriptions.revenueUSD, { compact: true })}
          </div>
          <div className="metric-label">Subscription Revenue</div>
          <div className="metric-sub">
            MRR: {formatMoney(subscriptions.mrrNGN, subscriptions.mrrUSD)}
          </div>
        </div>

        {/* Settlements Card */}
        <div className="metric-card settlements">
          <div className="metric-header">
            <Building size={20} />
            <span className="metric-badge">{settlements.processedThisPeriod} settled</span>
          </div>
          <div className="metric-value-large">
            {formatMoney(settlements.totalPaidOutNGN, settlements.totalPaidOutUSD, { compact: true })}
          </div>
          <div className="metric-label">Paid to Merchants</div>
          <div className="metric-sub">
            Pending: {formatMoney(settlements.pending.amountNGN, settlements.pending.amountUSD)}
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Monthly Trend */}
        <div className="chart-card large">
          <h3><BarChart3 size={18} /> Monthly Performance</h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => currency === 'USD' ? `$${(v/1000).toFixed(0)}k` : `₦${(v/1000000).toFixed(0)}M`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name.includes('Revenue') || name.includes('GMV')) {
                    return [currency === 'USD' ? `$${value.toLocaleString()}` : `₦${value.toLocaleString()}`, name];
                  }
                  return [value, name];
                }}
              />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey={currency === 'USD' ? 'gmvUSD' : 'gmvNGN'}
                name="GMV"
                fill="#1a6f63"
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="merchants"
                name="New Merchants"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="orders"
                name="Orders"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Breakdown */}
        <div className="chart-card">
          <h3><PieChart size={18} /> Revenue Breakdown</h3>
          <ResponsiveContainer width="100%" height={250}>
            <RechartsPie>
              <Pie
                data={revenueBreakdownData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {revenueBreakdownData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatMoney(value, value)} />
            </RechartsPie>
          </ResponsiveContainer>
        </div>

        {/* Merchant Tiers */}
        <div className="chart-card">
          <h3><Layers size={18} /> Merchant Tiers</h3>
          <ResponsiveContainer width="100%" height={250}>
            <RechartsPie>
              <Pie
                data={tierData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}`}
              >
                {tierData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </RechartsPie>
          </ResponsiveContainer>
          <div className="tier-legend">
            {tierData.map((tier, i) => (
              <span key={tier.name} style={{ color: tier.color }}>
                ● {tier.name}: {tier.value}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="details-section">
        {/* Merchant Details */}
        <div className="detail-card">
          <h3><Store size={18} /> Merchant Metrics</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Total Merchants</span>
              <span className="detail-value">{formatNumber(merchants.total)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Onboarded ({story.periodLabel})</span>
              <span className="detail-value">{formatNumber(merchants.onboardedThisPeriod)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Active (30 days)</span>
              <span className="detail-value">{formatNumber(merchants.active)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">With Orders</span>
              <span className="detail-value">{formatNumber(merchants.withOrders)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Activation Rate</span>
              <span className="detail-value">{merchants.activationRate}%</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Growth</span>
              <span className={`detail-value ${getGrowthClass(merchants.growth)}`}>
                {merchants.growth > 0 ? '+' : ''}{merchants.growth}%
              </span>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        <div className="detail-card">
          <h3><CreditCard size={18} /> Subscription Metrics</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Active Subscriptions</span>
              <span className="detail-value">{formatNumber(subscriptions.active)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Revenue ({story.periodLabel})</span>
              <span className="detail-value">{formatMoney(subscriptions.revenueNGN, subscriptions.revenueUSD)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Monthly Recurring</span>
              <span className="detail-value">{formatMoney(subscriptions.mrrNGN, subscriptions.mrrUSD)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Trial Conversions</span>
              <span className="detail-value">{formatNumber(subscriptions.trialConversions)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Churned</span>
              <span className="detail-value text-red">{formatNumber(subscriptions.churned)}</span>
            </div>
          </div>
          {subscriptions.byTier && subscriptions.byTier.length > 0 && (
            <div className="tier-breakdown">
              <h4>By Tier</h4>
              {subscriptions.byTier.map(tier => (
                <div key={tier.tier} className="tier-row">
                  <span>{tier.tier}</span>
                  <span>{tier.count} subscribers</span>
                  <span>{formatMoney(tier.revenueNGN, tier.revenueUSD)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order Channel Breakdown */}
        <div className="detail-card">
          <h3><Globe size={18} /> Sales Channels</h3>
          <div className="channel-list">
            {channelData.map(channel => (
              <div key={channel.name} className="channel-row">
                <div className="channel-name">
                  <span className="channel-dot" style={{ background: channel.color }}></span>
                  {channel.name || 'Direct'}
                </div>
                <div className="channel-stats">
                  <span>{formatNumber(channel.orders)} orders</span>
                  <span>{formatMoney(channel.gmv, channel.gmv)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Merchants */}
      <div className="top-merchants-section">
        <h3><Award size={18} /> Top Performing Merchants</h3>
        <div className="merchants-table">
          <div className="table-header">
            <span>Merchant</span>
            <span>Tier</span>
            <span>Orders</span>
            <span>GMV</span>
          </div>
          {topMerchants.map((merchant, i) => (
            <div key={merchant.id} className="table-row">
              <div className="merchant-info">
                <span className="rank">#{i + 1}</span>
                <div>
                  <span className="merchant-name">{merchant.storeName}</span>
                  <span className="merchant-email">{merchant.email}</span>
                </div>
              </div>
              <span className={`tier-badge tier-${merchant.tier?.toLowerCase()}`}>
                {merchant.tier}
              </span>
              <span>{formatNumber(merchant.orders)}</span>
              <span className="gmv-value">
                {formatMoney(merchant.gmvNGN, merchant.gmvUSD)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlatformInsights;
